import React, { useState, useMemo } from 'react';
import {
    createOrUpdateResidentialUnit,
    getResidentialUnitExtraData,
    updateResidentialUnit,
} from '@/services/residential-units';
import { Trans } from '@lingui/react/macro';
import { getPricingFields } from '@/services/projects';
import {
    PricingFieldValue,
    ResidentialUnitType,
    ResidentialUnitExtraData,
    ProjectTag,
    Order,
    OrderStatus,
    ChangedItem,
    ProjectTagType,
    OptionGroupDto,
    LinkingType,
} from '@/types';
import { ResidentialUnitEditProps } from '.';
import { Modal, ModalHeader, ModalBody, LoadingIndicator, ResidentialUnitCustomFieldDto, SimpleWizard, SimpleWizardStep } from '@ygtechnologies/bygr-frontend-component-library';
import { IState } from '@/state/IState';
import { useSelector } from 'react-redux';
import ResidentialUnitEditUnit from './ResidentialUnitEditUnit';
import { isEqual, differenceWith } from 'lodash-es';
import ResidentialUnitEditPricingFieldsConfirm from './ResidentialUnitEditPricingFieldsConfirm';
import ResidentialUnitEditTagsConfirm from './ResidentialUnitEditTagsConfirm';
import ResidentialUnitEditPricingFieldsOrders from './ResidentialUnitEditPricingFieldsOrders';
import { searchOrders } from '@/services/orders';
import ResidentialUnitTagsOrders from './ResidentialUnitTagsOrders';
// import { searchBuyerOptionGroups } from '@/services/buyer-options';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { getUnitVariables, getUnitVariableTypes, updateUnitVariables } from '@/services/project-unit-variables';
import { searchOptionGroups } from '@/services/options/option-groups';

enum Step {
    EditUnit,
    PricingFieldsOverview,
    PricingFieldsOrders,
    TagsBuyerOptions,
    TagsOrders,
}

const ResidentialUnitEdit: React.FC<ResidentialUnitEditProps> = ({ residentialUnitId, show, toggleOpen, onSave }) => {
    const projectId = useSelector((state: IState) => state.global.activeProject!.id);

    const [saving, setSaving] = useState(false);
    const [currentStep, setCurrentStep] = useState<Step>(Step.EditUnit);

    const [residentialUnit, setResidentialUnit] = useState<Partial<ResidentialUnitExtraData> | null>(null);

    const [pricingFieldValues, setPricingFieldValues] = useState<PricingFieldValue[] | null>(null);
    const [originalPricingFieldValues, setOriginalPricingFieldValues] = useState<PricingFieldValue[] | null>(null);

    const [pricingFieldsOrders, setPricingFieldsOrders] = useState<Order[] | null>(null);
    const [pricingFieldsBuyerOptionGroups, setPricingFieldsBuyerOptionGroups] = useState<OptionGroupDto[] | null>(null);

    const [originalTags, setOriginalTags] = useState<ProjectTag[]>([]);
    const [selectedTags, setSelectedTags] = useState<ProjectTag[]>([]);
    const [tagOrdersToCancel, setTagOrdersToCancel] = useState<Order[] | null>(null);
    const [tagsBuyerOptionGroups, setTagsBuyerOptionGroups] = useState<OptionGroupDto[] | null>(null);

    const [buyerOptionGroupToUnlinkIds, setBuyerOptionGroupToUnlinkIds] = useState<number[]>([]);
    const [unitVariables, setUnitVariables] = useState<ResidentialUnitCustomFieldDto[] | null>(null);

    const handleUnitChange = (change: ChangedItem<Partial<ResidentialUnitExtraData>>) => {
        setResidentialUnit(change.modified);
    };

    const changedPricingFields = useMemo(() => {
        if (!pricingFieldValues || !originalPricingFieldValues) return null;
        return differenceWith(pricingFieldValues, originalPricingFieldValues, isEqual).map((pf) => ({
            ...pf,
            value: pf.value!,
            oldValue: originalPricingFieldValues.find((opf) => opf.pricingFieldId === pf.pricingFieldId)!.value!,
        }));
    }, [pricingFieldValues, originalPricingFieldValues]);

    const removedTags = useMemo(() => {
        if (!selectedTags || !originalTags) return null;
        return differenceWith(originalTags, selectedTags, isEqual);
    }, [selectedTags, originalTags]);

    const steps = [
        Step.EditUnit,
        pricingFieldsBuyerOptionGroups && pricingFieldsBuyerOptionGroups.length > 0 ? Step.PricingFieldsOverview : null,
        pricingFieldsOrders && pricingFieldsOrders.length > 0 ? Step.PricingFieldsOrders : null,
        tagsBuyerOptionGroups && tagsBuyerOptionGroups.length > 0 ? Step.TagsBuyerOptions : null,
        tagOrdersToCancel && tagOrdersToCancel.length > 0 ? Step.TagsOrders : null,
    ].filter((e) => e !== null) as Step[];

    const reset = () => {
        setCurrentStep(Step.EditUnit);
        setResidentialUnit(null);

        setPricingFieldValues(null);
        setOriginalPricingFieldValues(null);
        setPricingFieldsBuyerOptionGroups(null);
        setPricingFieldsOrders(null);

        setSelectedTags([]);
        setOriginalTags([]);
        setTagOrdersToCancel([]);
        setTagsBuyerOptionGroups(null);

        setBuyerOptionGroupToUnlinkIds([]);
    };

    const handleOpen = async () => {
        reset();

        if (residentialUnitId) {
            const residentialUnit = await getResidentialUnitExtraData(residentialUnitId);
            setUnitVariables(await getUnitVariables(projectId, residentialUnitId));
            const pricingFieldValues = residentialUnit.pricingFields;
            setResidentialUnit(residentialUnit);
            setPricingFieldValues(pricingFieldValues);
            setOriginalPricingFieldValues(pricingFieldValues);

            const tags = residentialUnit.projectTags.map((pt) => ({
                ...pt,
                value: pt.id,
            }));
            setSelectedTags(tags);
            setOriginalTags(tags);
        } else {
            const pricingFields = await getPricingFields(projectId);
            setPricingFieldValues(
                pricingFields.map((x) => ({
                    pricingFieldId: x.id,
                    name: x.name,
                    value: null,
                }))
            );

            setResidentialUnit({ name: '', projectId, type: ResidentialUnitType.Apartment });

            setUnitVariables(
                (await getUnitVariableTypes(projectId)).map((x) => ({
                    id: x.id,
                    type: {
                        iconName: x.iconName,
                        name: x.name,
                        id: x.id,
                        dataType: x.dataType,
                    },
                    value: null,
                }))
            );
        }
    };

    const handleEditUnitContinue = async () => {
        if (!residentialUnit) {
            return;
        }

        setSaving(true);
        if (residentialUnit.id) {
            let hasPricingFieldGroups = false;
            let hasTagsGroups = false;

            if (!isEqual(pricingFieldValues, originalPricingFieldValues)) {
                const changedPricingFieldsIds = changedPricingFields!.map((pf) => pf.pricingFieldId);

                const groups = await searchOptionGroups({
                    pricingFieldIds: changedPricingFieldsIds,
                    unitIds: [residentialUnitId!],
                    projectId,
                });

                const orders = (
                    await searchOrders({
                        residentialUnitIds: [residentialUnitId!],
                        status: [OrderStatus.ReadyToStart],
                    })
                ).filter((x) => x.pricingFieldId && changedPricingFieldsIds.includes(x.pricingFieldId));

                setPricingFieldsOrders(orders);
                setPricingFieldsBuyerOptionGroups(groups);
                if (groups.length > 0) {
                    hasPricingFieldGroups = true;
                }
            }

            const removeProjectTags = removedTags ? removedTags.filter((x) => x.type === ProjectTagType.Room) : null;
            if (removeProjectTags && removeProjectTags.length > 0) {
                const removedTagIds = removeProjectTags.map((t) => t.id);
                // Removing a tag only cleans up directly-linked option groups. Room-linked groups stay
                // linked (the configurator manages those), and searchOptionGroups also returns groups
                // that merely share a tag with the unit, so keep only Direct groups linked to THIS unit.
                const groups = (
                    await searchOptionGroups({
                        tagIds: removedTagIds,
                        unitIds: [residentialUnit.id],
                        projectId,
                    })
                ).filter(
                    (g) =>
                        g.linkType === LinkingType.Direct &&
                        g.linkedUnits.some((u) => u.residentialUnitId === residentialUnit.id)
                );

                // Only look up orders when there are groups to unlink. searchOrders treats an empty
                // sourceOptionGroupIds as "no filter" and would otherwise return every ready-to-start order.
                let orders: Order[] = [];
                if (groups.length > 0) {
                    orders = await searchOrders({
                        residentialUnitIds: [residentialUnit.id!],
                        sourceOptionGroupIds: groups.map((g) => g.id),
                        status: [OrderStatus.ReadyToStart],
                    });
                }

                setTagOrdersToCancel(orders);
                setBuyerOptionGroupToUnlinkIds(groups.map((g) => g.id));
                setTagsBuyerOptionGroups(groups);
                if (groups.length > 0) {
                    hasTagsGroups = true;
                }
            }

            if (hasPricingFieldGroups || hasTagsGroups) {
                setSaving(false);
                if (hasPricingFieldGroups) {
                    setCurrentStep(Step.PricingFieldsOverview);
                } else {
                    setCurrentStep(Step.TagsBuyerOptions);
                }
                return;
            }
        }

        try {
            var savedUnit = await createOrUpdateResidentialUnit(
                residentialUnit,
                pricingFieldValues ? pricingFieldValues.map((x) => ({ ...x, value: x.value || 0 })) : null,
                selectedTags
            );
            await updateUnitVariables(
                projectId,
                savedUnit.id,
                unitVariables!.map((x) => ({ residentialUnitCustomFieldTypeId: x.type.id, value: x.value }))
            );
            toggleOpen();
            reset();
            triggerHandler(onSave);
        } finally {
            setSaving(false);
        }
    };

    const onTagBuyerOptionsContinue = async () => {
        let orders: Order[] = [];

        if (buyerOptionGroupToUnlinkIds.length > 0) {
            orders = await searchOrders({
                residentialUnitIds: [residentialUnit!.id!],
                sourceOptionGroupIds: buyerOptionGroupToUnlinkIds,
                status: [OrderStatus.ReadyToStart],
            });
        }

        setTagOrdersToCancel(orders);

        // Only advance to the orders step when there are orders to review; otherwise the flow is
        // complete, so save. Returning false stops SimpleWizard from also running its own
        // nextStep() against the dynamic steps array, which could otherwise leave currentStep on a
        // step that is no longer present and throw "Invalid step".
        if (orders.length > 0) {
            setCurrentStep(Step.TagsOrders);
        } else {
            await onUpdateSave();
        }

        return false;
    };

    const onUpdateSave = async () => {
        setSaving(true);
        try {
            await updateResidentialUnit(
                residentialUnit!.id!,
                residentialUnit!,
                pricingFieldValues ? pricingFieldValues.map((x) => ({ ...x, value: x.value || 0 })) : null,
                selectedTags,
                buyerOptionGroupToUnlinkIds
            );
            toggleOpen();
            reset();
            triggerHandler(onSave);
        } finally {
            setSaving(false);
        }
    };

    const renderContent = () => {
        if (!residentialUnit || !pricingFieldValues || !unitVariables) {
            return <LoadingIndicator />;
        }

        return (
            <>
                {currentStep === Step.EditUnit && (
                    <ResidentialUnitEditUnit
                        onChange={handleUnitChange}
                        pricingFieldValues={pricingFieldValues}
                        setUnitVariables={setUnitVariables}
                        unitVariables={unitVariables}
                        residentialUnit={residentialUnit}
                        saving={saving}
                        selectedTags={selectedTags}
                        setPricingFieldValues={setPricingFieldValues}
                        setSelectedTags={setSelectedTags}
                        toggleOpen={toggleOpen}
                        onSubmit={handleEditUnitContinue}
                    />
                )}
                {currentStep !== Step.EditUnit && (
                    <SimpleWizard
                        saveButtonProps={{ disabled: saving }}
                        onSubmit={onUpdateSave}
                        onStepChange={setCurrentStep}
                        currentStep={currentStep}
                        steps={steps}>
                        <SimpleWizardStep
                            header={<Trans>Options that are affected by the changes</Trans>}
                            stepNumber={Step.PricingFieldsOverview}>
                            <ResidentialUnitEditPricingFieldsConfirm
                                optionGroups={pricingFieldsBuyerOptionGroups!}
                                changedPricingFields={changedPricingFields!}
                            />
                        </SimpleWizardStep>

                        <SimpleWizardStep
                            header={<Trans>The orders that are affected by the changes</Trans>}
                            stepNumber={Step.PricingFieldsOrders}>
                            <ResidentialUnitEditPricingFieldsOrders
                                changedPricingFields={changedPricingFields!}
                                orders={pricingFieldsOrders!}
                            />
                        </SimpleWizardStep>

                        <SimpleWizardStep
                            onNext={onTagBuyerOptionsContinue}
                            header={<Trans>Edited tags</Trans>}
                            stepNumber={Step.TagsBuyerOptions}>
                            <ResidentialUnitEditTagsConfirm
                                residentialUnitName={residentialUnit.name || ''}
                                optionGroups={tagsBuyerOptionGroups!}
                                removedTags={removedTags!}
                                buyerOptionGroupToUnlinkIds={buyerOptionGroupToUnlinkIds}
                                onBuyerOptionGroupToUnlinkIdsChange={setBuyerOptionGroupToUnlinkIds}
                            />
                        </SimpleWizardStep>

                        <SimpleWizardStep
                            header={<Trans>Orders affected by editing tags</Trans>}
                            stepNumber={Step.TagsOrders}>
                            <ResidentialUnitTagsOrders orders={tagOrdersToCancel!} />
                        </SimpleWizardStep>
                    </SimpleWizard>
                )}
            </>
        );
    };

    return (
        <Modal size="xl" toggle={toggleOpen} isOpen={show} onClose={reset} onOpen={handleOpen}>
            <ModalHeader>
                {residentialUnitId ? <Trans>Edit residential unit</Trans> : <Trans>Create new unit</Trans>}
            </ModalHeader>
            <ModalBody>{renderContent()}</ModalBody>
        </Modal>
    );
};

export default ResidentialUnitEdit;
